import { useEffect, useCallback } from 'react'
import { useAuthStore } from '../store/authStore'
import { useChatStore } from '../store/chatStore'
import socketService from '../services/socketService'
import toast from 'react-hot-toast'

export const useSocket = () => {
  const { user, token } = useAuthStore()
  const { 
    addMessage, 
    updateMessage, 
    setTypingUsers, 
    setOnlineUsers,
    fetchChats,
    currentChat 
  } = useChatStore()

  // Connect socket when user is authenticated
  useEffect(() => {
    if (user && token) {
      const socket = socketService.connect(token)
      
      // Set up event listeners
      setupEventListeners()
      
      return () => {
        socketService.removeAllListeners()
      }
    } else {
      socketService.disconnect()
    }
  }, [user, token])

  // Setup socket event listeners
  const setupEventListeners = useCallback(() => {
    // New message received
    socketService.onNewMessage((message) => {
      addMessage(message)
      
      // Show notification if message is not from current user and not in current chat
      if (message.sender._id !== user?.id && message.chat !== currentChat?._id) {
        toast.success(`New message from ${message.sender.username}`)
      }
    })

    // User typing indicator
    socketService.onUserTyping(({ userId, username, isTyping }) => {
      setTypingUsers((prev) => {
        const newTypingUsers = new Map(prev)
        if (isTyping) {
          newTypingUsers.set(userId, username)
        } else {
          newTypingUsers.delete(userId)
        }
        return newTypingUsers
      })
    })

    // Message reaction update
    socketService.onReactionUpdate(({ messageId, reactions }) => {
      updateMessage(messageId, { reactions })
    })

    // Message read receipt
    socketService.onMessageRead(({ messageId, userId, username, readAt }) => {
      updateMessage(messageId, (prevMessage) => ({
        ...prevMessage,
        readBy: [
          ...prevMessage.readBy.filter(read => read.user._id !== userId),
          { user: { _id: userId, username }, readAt }
        ]
      }))
    })

    // User online status
    socketService.onUserOnline(({ userId, username, status }) => {
      setOnlineUsers((prev) => {
        const filtered = prev.filter(u => u.id !== userId)
        return [...filtered, { id: userId, username, status, isOnline: true }]
      })
    })

    // User offline status
    socketService.onUserOffline(({ userId, username, lastSeen }) => {
      setOnlineUsers((prev) => 
        prev.map(u => 
          u.id === userId 
            ? { ...u, isOnline: false, lastSeen }
            : u
        )
      )
    })

    // User status update
    socketService.onStatusUpdate(({ userId, status }) => {
      setOnlineUsers((prev) =>
        prev.map(u =>
          u.id === userId
            ? { ...u, status }
            : u
        )
      )
    })

    // New chat created
    socketService.onNewChat((chatData) => {
      // Refresh chats list
      fetchChats()
      toast.success('You were added to a new chat!')
    })

    // Socket errors
    socketService.onError((error) => {
      console.error('Socket error:', error)
      toast.error('Connection error occurred')
    })
  }, [addMessage, updateMessage, setTypingUsers, setOnlineUsers, fetchChats, user, currentChat])

  // Socket action methods
  const joinChat = useCallback((chatId) => {
    socketService.joinChat(chatId)
  }, [])

  const leaveChat = useCallback((chatId) => {
    socketService.leaveChat(chatId)
  }, [])

  const sendMessage = useCallback((messageData) => {
    socketService.sendMessage(messageData)
  }, [])

  const startTyping = useCallback((chatId) => {
    socketService.startTyping(chatId)
  }, [])

  const stopTyping = useCallback((chatId) => {
    socketService.stopTyping(chatId)
  }, [])

  const toggleReaction = useCallback((messageId, emoji) => {
    socketService.toggleReaction(messageId, emoji)
  }, [])

  const markMessageRead = useCallback((messageId, chatId) => {
    socketService.markMessageRead(messageId, chatId)
  }, [])

  const updateUserStatus = useCallback((status) => {
    socketService.updateStatus(status)
  }, [])

  const notifyChatCreated = useCallback((chatData) => {
    socketService.notifyChatCreated(chatData)
  }, [])

  // Check if socket is connected
  const isConnected = socketService.isSocketConnected()

  return {
    isConnected,
    joinChat,
    leaveChat,
    sendMessage,
    startTyping,
    stopTyping,
    toggleReaction,
    markMessageRead,
    updateUserStatus,
    notifyChatCreated,
  }
}